import { hall } from '@/hall'

export class UIUtil {
  // Restrict nickname length to 10 characters
  static restrictNickname(nickname: string, nickLen: number = 10): string {
    if (nickname == null) nickname = ''
    if (nickname.length > nickLen) {
      nickname = nickname.substring(0, nickLen - 3) + '...'
    }
    return nickname
  }

  // Check nickname validity
  static checkNickname(name: string): string | null {
    if (name) {
      name = name.trim()
      if (name !== '') return name
    }
    return null
  }

  // Format money using formatNum
  static formatMoney(num: number): string {
    return this.formatNum(num, 2)
  }

  // Format number with optional decimal precision
  static formatNum(num: number, decimals: number = 2, showZero: boolean = false): string {
    if (num == null) return 'None'

    num = parseFloat(num.toString())
    const [a, b] = [Math.floor(num), num % 1]
    if (b === 0) return a.toString()

    const formatStr = `%.${decimals}f`
    let str = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num)

    if (!showZero) {
      const zeroIndex = str.search(/0+$/)
      if (zeroIndex !== -1) {
        str = str.substring(0, zeroIndex)
      }
    }

    return str
  }

  // Format match time
  static formatMatchTime(time: number): string | undefined {
    if (!time) return undefined

    const date = new Date(time * 1000)
    const monthMap = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ]
    const hourTag = date.getHours() >= 12 ? 'PM' : 'AM'
    const str = `${String(date.getHours() % 12 || 12).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')} ${hourTag}, ${date.getDate()} ${monthMap[date.getMonth()]}`
    return str
  }

  // Format countdown timer
  static formatMatchCDTime(time: number): string {
    const hour = Math.floor(time / (60 * 60))
    time %= 60 * 60
    const minute = Math.floor(time / 60)
    const second = time % 60

    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
  }

  // Validate full name
  static checkFullName(fullName: string): boolean {
    if (!fullName || fullName.trim() === '') return false
    return /^[a-zA-Z\s]+$/.test(fullName) // Only letters and spaces allowed
  }

  // Validate PAN number
  static checkPANNumber(cardNum: string): boolean {
    return /^[A-Z0-9]{10}$/.test(cardNum || '')
  }

  // Validate Aadhaar number
  static checkAadhaarNumber(cardNum: string): boolean {
    return /^\d{12}$/.test(cardNum || '')
  }

  // Validate phone number (India)
  static checkPhoneNumber(num: string): boolean {
    return /^\d{10,12}$/.test(num || '')
  }

  // Validate Aadhaar OTP
  static checkAadhaarOTP(otp: string): boolean {
    return /^\d{6}$/.test(otp || '')
  }

  // Validate phone OTP
  static checkPhoneOTP(otp: string): boolean {
    return /^\d{4}$/.test(otp || '')
  }

  // Validate email
  static checkEmail(str: string): string | null {
    if (!str || typeof str !== 'string' || str.trim() === '') return null
    str = str.trim()
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str) ? str : null
  }

  // Validate e-wallet account number
  static checkEWalletAccountNumber(cardNum: string): boolean {
    const len = cardNum?.length || 0
    return len >= 3 && len <= 20 && /^[A-Za-z0-9!@#$%^&*(),.?":{}|<>]*$/.test(cardNum || '')
  }

  // Validate bank account number
  static checkBankAccountNumber(cardNum: string): boolean {
    const len = cardNum?.length || 0
    return len >= 8 && len <= 19 && /^\d+$/.test(cardNum || '')
  }

  // Validate IFSC code
  static checkBankIFSC(ifsc: string): [boolean, string?] {
    if (!ifsc || ifsc.length !== 11) {
      return [false, hall.Language.FillBankInfo_Error]
    } else if (ifsc[4] !== '0') {
      return [false, hall.Language.FillBankInfoFifth_Error]
    }
    return [true]
  }

  // Validate Easypaisa/Jazzcash card number
  static checkChannelCardNumber(cardNum: string): boolean {
    if (!cardNum || cardNum.length !== 10 || cardNum[0] !== '3') return false
    return /^\d+$/.test(cardNum)
  }

  // Validate Easypaisa/Jazzcash channel name number
  static checkChannelNameNumber(channelName: string): boolean {
    return /^\d*$/.test(channelName || '')
  }

  // 注册账号限制4~12位，由字母和数字组成，区分大小写，且为全平台唯一不能重复
  static checkValidUsername(username: string): boolean {
    // 检查输入是否为字符串类型
    if (typeof username !== 'string') return false
    if (!username || username.trim() === '') return false
    // 新增：检查是否包含空格字符
    if (/\s/.test(username)) return false
    const trimmedUsername = username.trim()
    return /^[a-zA-Z0-9]{4,12}$/.test(trimmedUsername)
  }

  // 注册账号密码限制6~12位，由字母和数字组成，区分大小写，密码默认展示为星号
  static checkValidPassword(pass: string): boolean {
    // 检查输入是否为字符串类型
    if (typeof pass !== 'string') return false
    if (!pass || pass.trim() === '') return false
    // 新增：检查是否包含空格字符
    if (/\s/.test(pass)) return false
    const trimmedPass = pass.trim()
    return /^[a-zA-Z0-9]{6,12}$/.test(trimmedPass)
  }

  // 09开头的纯数字，位数限制为10~11位。
  static checkValidPhone(num: string): boolean {
    const isTrue = /^09\d{8,9}$/.test(num || '')
    const isTrue2 = /^9\d{9,10}$/.test(num || '')
    if (isTrue || isTrue2) {
      return true
    }
    return false
  }

  // 位数限制为10位。以3开头，例如300 1234567，也可以是0300 1234567
  static checkPakPhone(num: string): boolean {
    const isTrue = /^03\d{9}$/.test(num || '')
    //let isTrue2 = /^3\d{9}$/.test(num || '');
    if (isTrue) {
      //|| isTrue2) { //|| isTrue2) {
      return true
    }
    return false
  }

  static validateString(input: string): boolean {
    const regex = /^[a-zA-Z\s]{2,20}$/
    return regex.test(input)
  }
}
